import React, { useEffect, useState } from "react";
import { useGetHistoricalViewQuery } from "../../../../redux/service/modules/dashboard";
import { useSelector } from "react-redux";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    LabelList,
    Legend,
} from "recharts";
import { ChartTooltip } from "../../../../components/common/ChartTooltip";
import { COLORS } from "../../../../utils/constant";
import { Box } from "@mui/material";
import Loader from "../../../../components/common/loader";
import NoDataAvailable from "../../../../components/common/NoDataAvailable";
import { useResponsiveStyles } from "../../../../styles/useResponsiveStyles";

const transformDataToArray = (data) => {
    const transformedData = Object.keys(data)
        .sort((a, b) => {
            return a.localeCompare(b);
        })
        .reduce((acc, quarter) => {
            const values = data[quarter];
            if (Object.keys(values).length > 0) {
                // Only process quarters with data
                const entry = { name: quarter };
                // Iterate over the keys of the values object
                Object.keys(values).forEach((key) => {
                    if (key !== "name") {
                        // Exclude the 'name' key if present
                        entry[key] = values[key]; // Directly assign the value
                    }
                });
                acc.push(entry); // Add the entry to the accumulator
            }
            return acc;
        }, []);

    return transformedData;
};

export const Historical = () => {
    const dateRange = useSelector((state) => state.date);
    const { data, isLoading, error } = useGetHistoricalViewQuery(dateRange);
    const [chartData, setChartData] = useState([]);
    const [keys, setKeys] = useState([]);
    const { getFontSize } = useResponsiveStyles();

    useEffect(() => {
        if (data) {
            const transformed = transformDataToArray(data);
            setChartData(transformed);
            // Extract keys dynamically from the first data object
            if (transformed.length > 0) {
                setKeys(
                    Object.keys(transformed[0]).filter((key) => key !== "name")
                );
            }
        }
    }, [data]);

    let staticContent;
    if (isLoading) {
        staticContent = <Loader />;
    } else if (error) {
        staticContent = "Error";
    } else if (
        chartData?.every((row) =>
            Object.keys(row).every(
                (key) => ["name"].includes(key) || row[key] === 0
            )
        )
    ) {
        staticContent = <NoDataAvailable />;
    }

    return (
        <Box>
            {staticContent ? (
                <div style={styles.staticContent}>{staticContent}</div>
            ) : (
                <Box className="h-[calc(100vh-9rem)] min-h-[400px] pb-4">
                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                        className={"mt-8"}
                    >
                        <BarChart
                            data={chartData}
                            margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 40,
                            }}
                            barSize={100}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                            />
                            <XAxis dataKey="name" tick={getFontSize("body1")} />
                            <YAxis
                                axisLine={true}
                                tickLine={true}
                                tick={getFontSize("body1")}
                            />
                            <Tooltip
                                content={<ChartTooltip />}
                                cursor={{ fill: "transparent" }}
                            />
                            {keys.map((key, index) => (
                                <Bar
                                    key={key}
                                    dataKey={key}
                                    name={`${key}`}
                                    stackId="a"
                                    fill={COLORS[index % COLORS.length]} // Use colors dynamically
                                >
                                    <LabelList
                                        dataKey={key}
                                        position="inside"
                                        style={{
                                            fontWeight: "bold",
                                            color: "#231F20",
                                        }}
                                        formatter={(value) =>
                                            value > 0 ? value : null
                                        }
                                    />
                                </Bar>
                            ))}
                            <Legend
                                verticalAlign="bottom"
                                iconType="circle"
                                wrapperStyle={{
                                    paddingTop: "20px",
                                    ...getFontSize("body1"),
                                }}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Box>
    );
};

const styles = {
    staticContent: {
        height: "80%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
};
